<template>
  <div class="filter-locations-container">
    <div class="search">
      <ElInput
        class="landing-search no-background"
        :placeholder="$t('search_destinations')"
        v-model="querySearch"
        v-loading="loading"
        @input="debounce(() => getLocations(true))"
        @keydown.enter.prevent="getLocations(true)"
      >
        <template #suffix>
          <Icons name="24px/search" @click="getLocations(true)" />
        </template>
      </ElInput>
    </div>
    <div class="locations">
      <div
        v-for="location in locations ?? []"
        :key="location.uuid"
        class="location"
      >
        <ElCheckbox
          class="landing-checkbox-rounded"
          v-model="location.selected"
          @change="selectedLocation(location)"
        />
        <span class="body2">
          {{ location.name }}
        </span>
      </div>
    </div>
    <div class="actions" v-if="showActions">
      <ElButton type="link dark" @click="clear()">
        {{ $t('clear') }}
      </ElButton>
      <ElButton type="primary dark" @click="$emit('filter')">
        {{ $t('apply') }}
      </ElButton>
    </div>
  </div>
</template>

<script>
import { ElLoading, ElMessageBox } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { cloneDeep } from 'lodash'

export default defineComponent({
  props: {
    showActions: {
      type: Boolean,
      default: false,
    }
  },
  setup() {
    const { width, height } = useBreakpoints()
    const { t } = useI18n()
    const { $sentry } = useNuxtApp()
    const router = useRouter()
    const route = useRoute()
    const config = useRuntimeConfig()
    const { debounce } = getTableInitialVariables()

    const initialVariables = []

    let objectToReturn = {
      width,
      height,
      t,
      router,
      route,
      config,
      $sentry,
      debounce
    }
    for (const variable of initialVariables) {
      objectToReturn = Object.assign(objectToReturn, variable)
    }

    return objectToReturn
  },
  data() {
    return {
      locations: null,
      loading: false,
      loadingDialog: null,
      filterLocations: {
        where: {
          type: {
            column: 'TYPE',
            value: 'city',
            operator: 'EQ'
          }
        },
        filterField: {
          hasExperiences: {
            column: 'HASEXPERIENCES',
            value: ''
          },
          hasAccommodations: {
            column: 'HASACCOMMODATIONS',
            value: ''
          },
          hasRestaurants: {
            column: 'HASRESTAURANTS',
            value: ''
          },
          hasActivities: {
            column: 'HASACTIVITIES',
            value: ''
          },
          hasTours: {
            column: 'HASTOURS',
            value: ''
          }
        }
      },
      querySearch: null
    }
  },
  mounted() {
    this.getLocations()
  },
  methods: {
    async getLocations(refetch = false) {
      try {
        if (this.route.name === 'experiences') {
          this.filterLocations.filterField.hasExperiences.value = true
        }
        if (this.route.name === 'accommodations') {
          this.filterLocations.filterField.hasAccommodations.value = true
        }
        if (this.route.name === 'restaurants') {
          this.filterLocations.filterField.hasRestaurants.value = true
        }
        if (this.route.name === 'activities') {
          this.filterLocations.filterField.hasActivities.value = true
        }
        if (this.route.name === 'tours') {
          this.filterLocations.filterField.hasTours.value = true
        }
        this.loadingDialog = ElLoading.service({
          fullscreen: true,
          background: '#F7F7F7',
          svg: useLoaderSvg(),
          svgViewBox: useLoaderSvgViewBox()
        })
        const locations = await apolloLocations(
          {
            sortBy: 'NAME',
            sortDesc: false,
            search: this.querySearch,
            page: 1,
            first: 500
          },
          true,
          this.filterLocations,
          refetch ? 'network-only' : null
        )

        this.locations = locations?.getAllLocations?.data?.map((i) => {
          return {
            ...i,
            selected: false
          }
        })
      } catch (err) {
        this.$showError(err, this.t)
        this.$sentry(err, 'getLocations')
      } finally {
        this.loadingDialog.close()
      }
    },
    selectedLocation(location) {
      this.locations = this.locations.map((i) => {
        if (location.uuid === i.uuid) {
          return {
            ...i,
            selected: i.selected ? true : false
          }
        }
        return i
      })
      this.$emit(
        'selectedLocation',
        this.locations.filter((i) => i.selected).map((i) => i.name) ?? []
      )
    },
    clear() {
      this.locations = this.locations.map((i) => {
        return {
          ...i,
          selected: false
        }
      })
      this.querySearch = null
      this.$emit('selectedLocation', [])
      this.$emit('filter')
    }
  }
})
</script>

<style scoped lang="scss">
.filter-locations-container {
  .search {
    margin-bottom: 10px;
  }
  .locations {
    max-height: 300px;
    padding-bottom: 10px;
    overflow-y: auto;
    .location {
      display: flex;
      span.body2 {
        position: relative;
        top: 8px;
        margin-left: 8px;
      }

      padding-top: 16px;
    }
  }
  .actions {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>